import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import customPagination from '../../components/CustomPagination/CustomPagination';
import { userRequest } from '../../utils/requestMethods';
import { useStateContext } from '../../contexts/ContextProvider';
import { generateSchoolSessions } from '../../utils/schoolYearUtils';
import { MdSearch } from 'react-icons/md';
import { HiOutlineUsers, HiOutlineCalendarDays, HiOutlineChartBar } from 'react-icons/hi2';
import { Link } from 'react-router-dom';
import { NoDataComponent, SkeletonTable } from '../../components';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import emmymaxLogo from '../../data/logo/logo.png';

const customStyles = {
    headRow: {
        style: {
            backgroundColor: 'rgb(248, 250, 252)',
            fontWeight: "bold",
        },
    },
};

export default function AttendanceManagement({ history }) {
    const { user, school } = useStateContext();
    console.log(school);
    const schoolTerm = school?.settings?.current_term;
    const schoolSession = school?.settings?.current_session;
    const [filterText, setFilterText] = useState('');
    const [studentAttendance, setStudentAttendance] = useState([]);
    const [rawAttendanceData, setRawAttendanceData] = useState([]); // Store raw attendance data for PDF export
    const [totals, setTotals] = useState({ totalStudents: 0, totalAttendanceRate: 0, totalDays: 0, totalPresentDays: 0, totalAbsentDays: 0 });
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [classes, setClasses] = useState([]);
    const [dropdown, setDropdown] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState('all');
    const [selectedClass, setSelectedClass] = useState(() => {
        const savedClass = JSON.parse(localStorage.getItem('selectedClass'));
        return savedClass || { id: null, name: 'All classes' };
    });
    
    // Term and Session filter states
    const [termDropdown, setTermDropdown] = useState(false);
    const [sessionDropdown, setSessionDropdown] = useState(false);
    const [selectedTerm, setSelectedTerm] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem('attendanceSelectedTerm'));
            if (saved && typeof saved === 'object' && saved.id && saved.name) {
                return { id: String(saved.id), name: String(saved.name) };
            }
        } catch (e) {
            // Invalid JSON, ignore
        }
        return { id: String(schoolTerm || '1'), name: `Term ${schoolTerm || '1'}` };
    });
    const [selectedSession, setSelectedSession] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem('attendanceSelectedSession'));
            if (saved && typeof saved === 'object') {
                // Handle both {id, name} and {label, value} structures
                const sessionId = saved.id || saved.value || '';
                const sessionName = saved.name || saved.label || sessionId;
                if (sessionId) {
                    return { id: String(sessionId), name: String(sessionName) };
                }
            }
        } catch (e) {
            // Invalid JSON, ignore
        }
        const defaultSession = String(schoolSession || '2024/2025');
        return { id: defaultSession, name: defaultSession };
    });

    // Generate school sessions
    const schoolSessionsData = generateSchoolSessions(2020, 10);
    const schoolSessions = schoolSessionsData.map(s => typeof s === 'string' ? s : (s.value || s.label || s));

    // Get available terms from school settings
    const availableTerms = school?.settings?.terms?.map(term => {
        const termId = typeof term === 'object' && term.term ? String(term.term) : String(term || '1');
        return {
            id: termId,
            name: `Term ${termId}`
        };
    }).filter(term => term.id && term.name) || [
        { id: '1', name: 'Term 1' },
        { id: '2', name: 'Term 2' },
        { id: '3', name: 'Term 3' }
    ];

    const handleDropdown = () => setDropdown(!dropdown);
    const handleTermDropdown = () => setTermDropdown(!termDropdown);
    const handleSessionDropdown = () => setSessionDropdown(!sessionDropdown);

    // Records without a period field are treated as morning (backward compatibility).
    // Attendance rate is always based on morning period only.
    const processAttendanceData = (students) => {
        return students.map(student => {
            const allRecords = student.attendance.flatMap(record => record.records);

            // Deduplicate morning records by date (no-period → morning)
            const morningDateMap = new Map();
            allRecords.filter(r => !r.period || r.period === 'morning').forEach(r => {
                const dateStr = r.date.split('T')[0];
                if (!morningDateMap.has(dateStr)) morningDateMap.set(dateStr, r.status);
            });

            // Deduplicate afternoon records by date
            const afternoonDateMap = new Map();
            allRecords.filter(r => r.period === 'afternoon').forEach(r => {
                const dateStr = r.date.split('T')[0];
                if (!afternoonDateMap.has(dateStr)) afternoonDateMap.set(dateStr, r.status);
            });

            const morningTotal = morningDateMap.size;
            const morningAttended = Array.from(morningDateMap.values()).filter(s => s === 1).length;
            const morningAbsent = morningTotal - morningAttended;

            const afternoonTotal = afternoonDateMap.size;
            const afternoonAttended = Array.from(afternoonDateMap.values()).filter(s => s === 1).length;
            const afternoonAbsent = afternoonTotal - afternoonAttended;

            const attendanceRate = morningTotal > 0 ? ((morningAttended / morningTotal) * 100).toFixed(2) : 0;

            return {
                key: student.student.id,
                id: student.student.id,
                name: `${student.student.lastname} ${student.student.firstname}` || "Unknown",
                lastname: student.student.lastname || "",
                student: `${student.student}`,
                class: student.class || "N/A",
                AttendanceRate: `${attendanceRate}%`,
                MorningAttended: morningAttended,
                MorningAbsent: morningAbsent,
                AfternoonAttended: afternoonAttended,
                AfternoonAbsent: afternoonAbsent,
            };
        });
    };

    useEffect(() => {
        const fetchClasses = async () => {
            if (school) {
                try {
                    setLoading(true);
                    const { data } = await userRequest.get(`classes?code=${school?.code}`);
                    setClasses(data.classes || []);
                } catch (error) {
                    console.error("Error fetching classes:", error);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchClasses();
    }, [school]);

    const fetchAttendance = async () => {
        if (!selectedClass.id) return;
        
        setLoading(true);
        try {
            const term = selectedTerm.id || schoolTerm || '1';
            const session = selectedSession.id || schoolSession || '2024/2025';
            
            // Get term dates for selected term - handle both string and number comparisons
            const termData = school?.settings?.terms?.find(t => {
                const termValue = typeof t.term === 'number' ? String(t.term) : String(t.term || '');
                return termValue === String(term);
            });
            const startDate = termData?.start_date || '';
            const endDate = termData?.end_date || '';
            
            console.log('Fetching attendance with:', { term, session, startDate, endDate, termData });
            
            const { data } = await userRequest.get(`/attendance/class/${selectedClass.id || ''}?school_id=${school?._id}&term=${term}&session=${session}&from=${startDate}&to=${endDate}&limit=50`);
            console.log(data?.students);
            const students = data?.students || [];
            const processedData = processAttendanceData(students);
            setStudentAttendance(processedData);
            setRawAttendanceData(students);
        } catch (error) {
            console.error("Error fetching attendance:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAttendanceStats = async () => {
        if (!selectedClass.id || !school?._id) return;
        setStatsLoading(true);
        try {
            const term = selectedTerm.id || schoolTerm || '1';
            const session = selectedSession.id || schoolSession || '2024/2025';
            const params = new URLSearchParams({
                class_id: selectedClass.id,
                school_id: school._id,
                term,
                session,
            });
            if (selectedPeriod !== 'all') params.append('period', selectedPeriod);

            const { data } = await userRequest.get(`/attendance/stats?${params.toString()}`);
            setTotals({
                totalStudents: data.total_students ?? 0,
                totalAttendanceRate: data.total_attendance_rate ?? 0,
                totalDays: data.total_days ?? 0,
                totalPresentDays: data.total_present_days ?? 0,
                totalAbsentDays: data.total_absent_days ?? 0,
            });
        } catch (error) {
            console.error('Error fetching attendance stats:', error);
        } finally {
            setStatsLoading(false);
        }
    };

    useEffect(() => {
        if (selectedClass.id && school?._id) {
            fetchAttendance();
            fetchAttendanceStats();
        }
    }, [school?._id, selectedClass.id, selectedTerm.id, selectedSession.id, selectedPeriod]);

    // selectedPeriod only affects PDF export; table always shows both AM and PM columns

    const handleClassSelection = (id, name) => {
        const newSelection = { id, name };
        setSelectedClass(newSelection);
        localStorage.setItem('selectedClass', JSON.stringify(newSelection));
        setDropdown(false);
    };

    const handleTermSelection = (id, name) => {
        const newSelection = { id, name };
        setSelectedTerm(newSelection);
        localStorage.setItem('attendanceSelectedTerm', JSON.stringify(newSelection));
        setTermDropdown(false);
    };

    const handleSessionSelection = (id, name) => {
        const newSelection = { id, name };
        setSelectedSession(newSelection);
        localStorage.setItem('attendanceSelectedSession', JSON.stringify(newSelection));
        setSessionDropdown(false);
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdown || termDropdown || sessionDropdown) {
                if (!event.target.closest('.filter-dropdown')) {
                    setDropdown(false);
                    setTermDropdown(false);
                    setSessionDropdown(false);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdown, termDropdown, sessionDropdown]);

    const columns = [
        { name: "Name", selector: row => row.name || '' },
        { name: "Class", selector: row => selectedClass?.name || 'All classes' },
        { name: "Attendance Rate", selector: row => row.AttendanceRate || '0%' },
        { name: "AM Days In", selector: row => String(row.MorningAttended || 0) },
        { name: "AM Days Out", cell: row => <p className="text-[#D05151]">{row.MorningAbsent || 0}</p> },
        { name: "PM Days In", selector: row => String(row.AfternoonAttended || 0) },
        { name: "PM Days Out", cell: row => <p className="text-[#D05151]">{row.AfternoonAbsent || 0}</p> },
        {
            name: "Action",
            cell: row => (
                <Link to={{ pathname: `/students/view/${row.id}/${selectedClass.id}`, state: { student: row.name } }}>
                    <button className="text-[#3F3DA5] hover:font-semibold">View Attendance</button>
                </Link>
            ),
        },
    ];

    const filteredData = studentAttendance?.filter((item) =>
        (item.name?.toLowerCase().includes(filterText.toLowerCase()) || '') 
    ).sort((a, b) => {
        // Sort by lastname alphabetically
        const lastNameA = (a.lastname || '').toLowerCase();
        const lastNameB = (b.lastname || '').toLowerCase();
        return lastNameA.localeCompare(lastNameB);
    });

    // Transform attendance data for PDF export - always stores morning and afternoon separately
    const transformAttendanceForPDF = () => {
        if (!rawAttendanceData || rawAttendanceData.length === 0) return { weeks: [], students: [] };

        const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
        const weekMap = new Map();

        rawAttendanceData.forEach(student => {
            student.attendance?.forEach(dayRecord => {
                const day = dayRecord.day;
                dayRecord.records?.forEach(record => {
                    const week = record.week;
                    const date = new Date(record.date);
                    const dateStr = date.toISOString().split('T')[0];

                    if (!weekMap.has(week)) {
                        weekMap.set(week, { weekNumber: week, dates: new Map() });
                    }
                    const weekData = weekMap.get(week);
                    if (!weekData.dates.has(day) || new Date(weekData.dates.get(day)) < date) {
                        weekData.dates.set(day, dateStr);
                    }
                });
            });
        });

        const sortedWeeks = Array.from(weekMap.keys()).sort((a, b) => a - b).map(weekNum => {
            const weekData = weekMap.get(weekNum);
            const dates = Array.from(weekData.dates.values()).map(d => new Date(d)).sort((a, b) => a - b);
            const minDate = dates[0] || null;
            const maxDate = dates[dates.length - 1] || null;
            return {
                week: weekNum,
                dates: weekData.dates,
                dateRange: minDate && maxDate ? {
                    start: minDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
                    end: maxDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
                } : null
            };
        });

        const students = rawAttendanceData.map(student => {
            const studentName = `${student.student.firstname} ${student.student.lastname}`;
            const attendanceMatrix = {};

            sortedWeeks.forEach(({ week }) => {
                attendanceMatrix[week] = {};
                dayNames.forEach(day => {
                    attendanceMatrix[week][day] = { morning: null, afternoon: null };
                });
            });

            student.attendance?.forEach(dayRecord => {
                const dayName = dayNames[dayRecord.day - 1];
                dayRecord.records?.forEach(record => {
                    const week = record.week;
                    const period = record.period || 'morning';
                    if (attendanceMatrix[week]?.[dayName] && (period === 'morning' || period === 'afternoon')) {
                        attendanceMatrix[week][dayName][period] = record.status;
                    }
                });
            });

            return { id: student.student.id, name: studentName, attendance: attendanceMatrix };
        });

        return { weeks: sortedWeeks, students };
    };

    // Export attendance to PDF - one week per page
    const exportAttendanceToPDF = async () => {
        if (!rawAttendanceData || rawAttendanceData.length === 0) {
            alert('No attendance data available to export');
            return;
        }
        if (!selectedClass.id) {
            alert('Please select a class first');
            return;
        }

        setExporting(true);
        try {
            const showAll = selectedPeriod === 'all';
            const filterPeriod = showAll ? null : selectedPeriod;

            // Landscape for "all" (11 cols) to give enough width; portrait for single period (6 cols)
            const orientation = showAll ? 'l' : 'p';
            const doc = new jsPDF(orientation, 'mm', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 15;

            const { weeks, students } = transformAttendanceForPDF();

            if (weeks.length === 0 || students.length === 0) {
                alert('No attendance data to export');
                return;
            }

            const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
            const statusLabel = (s) => s === 1 ? 'P' : s === 2 ? 'A' : '-';

            const addPageHeader = (pageNum, weekInfo = null) => {
                const headerY = 10;
                try {
                    const logoSource = school?.picture?.path || emmymaxLogo;
                    doc.addImage(logoSource, logoSource.includes('http') ? 'PNG' : 'JPG', margin, headerY, 25, 18);
                } catch (e) {}

                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.text(school?.school_name || 'School Name', margin + 30, headerY + 6);

                doc.setFontSize(11);
                doc.text(`Class: ${selectedClass.name || 'N/A'}`, margin + 30, headerY + 12);

                doc.setFontSize(9);
                doc.setFont('helvetica', 'normal');
                const periodLabel = showAll ? 'All Periods (Morning & Afternoon)' : selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1);
                doc.text(`Term: ${selectedTerm.name || 'N/A'} | Session: ${selectedSession.name || 'N/A'} | Period: ${periodLabel}`, margin + 30, headerY + 17);

                if (weekInfo) {
                    doc.setFontSize(13);
                    doc.setFont('helvetica', 'bold');
                    const weekTitle = `Week ${weekInfo.week}${weekInfo.dateRange ? ` (${weekInfo.dateRange.start} – ${weekInfo.dateRange.end})` : ''}`;
                    doc.text(weekTitle, pageWidth / 2, headerY + 24, { align: 'center' });
                }

                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.text(`Page ${pageNum}`, pageWidth - margin, headerY + 5, { align: 'right' });
                if (pageNum === 1) {
                    doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, pageWidth - margin, headerY + 10, { align: 'right' });
                }
            };

            // Build column definitions depending on period mode
            const buildColumns = (availableWidth) => {
                if (showAll) {
                    // 1 name col + 10 AM/PM cols
                    const nameW = 50;
                    const cellW = (availableWidth - nameW) / 10;
                    const cols = [{ header: 'Student Name', width: nameW }];
                    dayNames.forEach(day => {
                        cols.push({ header: `${day}\nAM`, width: cellW, day, period: 'morning' });
                        cols.push({ header: `${day}\nPM`, width: cellW, day, period: 'afternoon' });
                    });
                    return cols;
                } else {
                    // 1 name col + 5 day cols
                    const nameW = 60;
                    const cellW = (availableWidth - nameW) / 5;
                    const cols = [{ header: 'Student Name', width: nameW }];
                    dayNames.forEach(day => {
                        cols.push({ header: day, width: cellW, day, period: filterPeriod });
                    });
                    return cols;
                }
            };

            weeks.forEach((weekInfo, weekIndex) => {
                if (weekIndex > 0) doc.addPage();

                const week = weekInfo.week;
                const weekStartPage = doc.internal.getCurrentPageInfo().pageNumber;
                addPageHeader(weekStartPage, weekInfo);

                const availableWidth = pageWidth - margin * 2;
                const cols = buildColumns(availableWidth);

                const headers = cols.map(c => c.header);
                const tableData = students.map(student => {
                    return cols.map((col, i) => {
                        if (i === 0) return student.name;
                        const cell = student.attendance[week]?.[col.day];
                        return cell ? statusLabel(cell[col.period]) : '-';
                    });
                });

                const columnStyles = {};
                cols.forEach((col, i) => {
                    columnStyles[i] = {
                        cellWidth: col.width,
                        halign: i === 0 ? 'left' : 'center',
                        fontStyle: i === 0 ? 'bold' : 'normal',
                    };
                });

                doc.autoTable({
                    head: [headers],
                    body: tableData,
                    startY: 40,
                    margin: { left: margin, right: margin, top: 40 },
                    styles: { fontSize: 7.5, cellPadding: 1.5, lineWidth: 0.1 },
                    headStyles: {
                        fillColor: [55, 65, 81],
                        textColor: 255,
                        fontStyle: 'bold',
                        fontSize: 7.5,
                        halign: 'center',
                        cellPadding: 2,
                    },
                    columnStyles,
                    alternateRowStyles: { fillColor: [248, 250, 252] },
                    didDrawPage: (data) => {
                        const currentPageNum = doc.internal.getCurrentPageInfo().pageNumber;
                        if (currentPageNum > weekStartPage) addPageHeader(currentPageNum, weekInfo);
                    },
                });

                // Week summary
                let amPresent = 0, amAbsent = 0, pmPresent = 0, pmAbsent = 0;
                students.forEach(student => {
                    dayNames.forEach(day => {
                        const cell = student.attendance[week]?.[day];
                        if (!cell) return;
                        if (cell.morning === 1) amPresent++; else if (cell.morning === 2) amAbsent++;
                        if (cell.afternoon === 1) pmPresent++; else if (cell.afternoon === 2) pmAbsent++;
                    });
                });

                const finalY = doc.lastAutoTable.finalY + 5;
                const summaryTarget = finalY < pageHeight - 15 ? finalY : (() => { doc.addPage(); addPageHeader(doc.internal.getCurrentPageInfo().pageNumber, weekInfo); return 35; })();

                doc.setFontSize(8);
                doc.setFont('helvetica', 'bold');
                doc.text('Summary:', margin, summaryTarget);
                doc.setFont('helvetica', 'normal');

                const summaryText = showAll
                    ? `Students: ${students.length} | Morning — Present: ${amPresent}, Absent: ${amAbsent} | Afternoon — Present: ${pmPresent}, Absent: ${pmAbsent}`
                    : (() => {
                        const p = filterPeriod === 'morning' ? amPresent : pmPresent;
                        const a = filterPeriod === 'morning' ? amAbsent : pmAbsent;
                        const rate = (p + a) > 0 ? ((p / (p + a)) * 100).toFixed(1) : 0;
                        return `Students: ${students.length} | Present: ${p} | Absent: ${a} | Rate: ${rate}%`;
                    })();
                doc.text(summaryText, margin + 20, summaryTarget);

                if (weekIndex === 0 && finalY < pageHeight - 20) {
                    doc.setFont('helvetica', 'bold');
                    doc.text('Legend:', margin, finalY + 8);
                    doc.setFont('helvetica', 'normal');
                    doc.text('P = Present | A = Absent | - = Not Marked', margin + 18, finalY + 8);
                }
            });

            const periodLabel = showAll ? 'All' : selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1);
            const fileName = `${selectedClass.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'class'}_${periodLabel}_Attendance_${selectedTerm.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'term'}_${selectedSession.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'session'}.pdf`;
            doc.save(fileName);
        } catch (error) {
            console.error('Error exporting PDF:', error);
            alert('Failed to export PDF. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="w-full flex items-center justify-center pt-24 pb-14 md:py-9 relative">
            <div className="bg-transparent pt-4 md:pt-0 w-full pb-16 px-9">
                <h2 className="text-2xl font-bold mb-3">Attendance</h2>
                <div className='grid grid-cols-2 lg:grid-cols-5 gap-3'>
                    {/* Total Students */}
                    <div className='bg-white rounded-2xl border border-[#E7E7E7] overflow-hidden'>
                        <div className='px-4 py-4'>
                            <div className='flex items-center justify-between mb-3'>
                                <p className='text-xs font-medium text-gray-400 uppercase tracking-wide'>Students</p>
                                <div className='w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center'>
                                    <HiOutlineUsers className='w-4 h-4 text-blue-500' />
                                </div>
                            </div>
                            {statsLoading
                                ? <div className='h-7 w-12 bg-gray-100 rounded-lg animate-pulse' />
                                : <p className='text-2xl font-bold text-gray-800'>{totals.totalStudents || 0}</p>
                            }
                        </div>
                    </div>

                    {/* Attendance Rate */}
                    <div className='bg-white rounded-2xl border border-[#E7E7E7] overflow-hidden'>
                        <div className='px-4 py-4'>
                            <div className='flex items-center justify-between mb-3'>
                                <p className='text-xs font-medium text-gray-400 uppercase tracking-wide'>Rate</p>
                                <div className='w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center'>
                                    <HiOutlineChartBar className='w-4 h-4 text-indigo-500' />
                                </div>
                            </div>
                            {statsLoading
                                ? <div className='h-7 w-16 bg-gray-100 rounded-lg animate-pulse' />
                                : <>
                                    <p className='text-2xl font-bold text-gray-800'>{totals.totalAttendanceRate || 0}%</p>
                                    <div className='mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden'>
                                        <div
                                            className='h-full bg-indigo-500 rounded-full transition-all duration-500'
                                            style={{ width: `${Math.min(totals.totalAttendanceRate || 0, 100)}%` }}
                                        />
                                    </div>
                                </>
                            }
                        </div>
                    </div>

                    {/* Total Days */}
                    <div className='bg-white rounded-2xl border border-[#E7E7E7] overflow-hidden'>
                        <div className='px-4 py-4'>
                            <div className='flex items-center justify-between mb-3'>
                                <p className='text-xs font-medium text-gray-400 uppercase tracking-wide'>Total Days</p>
                                <div className='w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center'>
                                    <HiOutlineCalendarDays className='w-4 h-4 text-orange-400' />
                                </div>
                            </div>
                            {statsLoading
                                ? <div className='h-7 w-12 bg-gray-100 rounded-lg animate-pulse' />
                                : <p className='text-2xl font-bold text-gray-800'>{totals.totalDays || 0}</p>
                            }
                        </div>
                    </div>

                    {/* Days Present */}
                    <div className='bg-white rounded-2xl border border-[#E7E7E7] overflow-hidden'>
                        <div className='px-4 py-4'>
                            <div className='flex items-center justify-between mb-3'>
                                <p className='text-xs font-medium text-gray-400 uppercase tracking-wide'>Present</p>
                                <div className='w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center'>
                                    <svg className='w-4 h-4 text-emerald-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
                                        <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
                                    </svg>
                                </div>
                            </div>
                            {statsLoading
                                ? <div className='h-7 w-12 bg-gray-100 rounded-lg animate-pulse' />
                                : <p className='text-2xl font-bold text-emerald-600'>{totals.totalPresentDays || 0}</p>
                            }
                        </div>
                    </div>

                    {/* Days Absent */}
                    <div className='bg-white rounded-2xl border border-[#E7E7E7] overflow-hidden'>
                        <div className='px-4 py-4'>
                            <div className='flex items-center justify-between mb-3'>
                                <p className='text-xs font-medium text-gray-400 uppercase tracking-wide'>Absent</p>
                                <div className='w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center'>
                                    <svg className='w-4 h-4 text-red-400' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
                                        <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
                                    </svg>
                                </div>
                            </div>
                            {statsLoading
                                ? <div className='h-7 w-12 bg-gray-100 rounded-lg animate-pulse' />
                                : <p className='text-2xl font-bold text-red-500'>{totals.totalAbsentDays || 0}</p>
                            }
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-2xl mt-6 px-6 py-7">
                    <div className='w-full'>
                        <div className='flex flex-wrap items-center justify-between mb-6'>
                            {/* Period filter */}
                            <div className='flex items-center gap-2 mb-4 w-full'>
                                {['all', 'morning', 'afternoon'].map(p => (
                                    <button
                                        key={p}
                                        onClick={() => setSelectedPeriod(p)}
                                        className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                                            selectedPeriod === p
                                                ? 'bg-indigo-700 text-white border-indigo-700'
                                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                        }`}
                                    >
                                        {p === 'all' ? 'All Periods' : p.charAt(0).toUpperCase() + p.slice(1)}
                                    </button>
                                ))}
                            </div>
                            <div className='flex flex-wrap items-center gap-3 filter-dropdown'>
                            <div className='relative w-max text-[#4543B5] text-xs'>
                                <button type='button' onClick={handleDropdown} className='w-full text-sm font-bold flex items-center justify-between bg-transparent hover:bg-blue-50 border-1 border-[#C5C5E8] py-2 px-3 rounded-full'>
                                        <p className='mr-2'>Class: {typeof selectedClass?.name === 'string' ? selectedClass.name : (selectedClass?.name || 'All classes')}</p>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline ml-2 icon icon-tabler icons-tabler-outline icon-tabler-chevron-down"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M6 9l6 6l6 -6" /></svg>
                                </button>
                                {dropdown && (
                                        <div className="w-full absolute bg-white border rounded shadow-md mt-2 z-10 max-h-60 overflow-y-auto">
                                            {classes.map(classe => {
                                                const className = typeof classe.class_name === 'string' ? classe.class_name : String(classe.class_name || '');
                                                return (
                                            <div
                                                key={classe._id}
                                                        onClick={() => handleClassSelection(classe._id, className)}
                                                        className="cursor-pointer text-md px-4 py-2 hover:bg-gray-100"
                                                    >
                                                        {className}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                
                                <div className='relative w-max text-[#4543B5] text-xs'>
                                    <button type='button' onClick={handleTermDropdown} className='w-full text-sm font-bold flex items-center justify-between bg-transparent hover:bg-blue-50 border-1 border-[#C5C5E8] py-2 px-3 rounded-full'>
                                        <p className='mr-2'>Term: {typeof selectedTerm?.name === 'string' ? selectedTerm.name : `Term ${selectedTerm?.id || schoolTerm || '1'}`}</p>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline ml-2 icon icon-tabler icons-tabler-outline icon-tabler-chevron-down"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M6 9l6 6l6 -6" /></svg>
                                    </button>
                                    {termDropdown && (
                                        <div className="w-full absolute bg-white border rounded shadow-md mt-2 z-10 max-h-60 overflow-y-auto">
                                            {availableTerms.map(term => {
                                                const termName = typeof term.name === 'string' ? term.name : `Term ${term.id || '1'}`;
                                                return (
                                                    <div
                                                        key={term.id}
                                                        onClick={() => handleTermSelection(String(term.id), termName)}
                                                        className="cursor-pointer text-md px-4 py-2 hover:bg-gray-100"
                                                    >
                                                        {termName}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div className='relative w-max text-[#4543B5] text-xs'>
                                    <button type='button' onClick={handleSessionDropdown} className='w-full text-sm font-bold flex items-center justify-between bg-transparent hover:bg-blue-50 border-1 border-[#C5C5E8] py-2 px-3 rounded-full'>
                                        <p className='mr-2'>Session: {typeof selectedSession?.name === 'string' ? selectedSession.name : (selectedSession?.id || schoolSession || '2024/2025')}</p>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline ml-2 icon icon-tabler icons-tabler-outline icon-tabler-chevron-down"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M6 9l6 6l6 -6" /></svg>
                                    </button>
                                    {sessionDropdown && (
                                        <div className="w-full absolute bg-white border rounded shadow-md mt-2 z-10 max-h-60 overflow-y-auto">
                                            {schoolSessions.map(session => {
                                                const sessionStr = typeof session === 'string' ? session : String(session || '');
                                                return (
                                                    <div
                                                        key={sessionStr}
                                                        onClick={() => handleSessionSelection(sessionStr, sessionStr)}
                                                className="cursor-pointer text-md px-4 py-2 hover:bg-gray-100"
                                            >
                                                        {sessionStr}
                                            </div>
                                                );
                                            })}
                                    </div>
                                )}
                                </div>
                            </div>
                            <div className='flex flex-wrap mt-4 md:mt-0 w-full md:w-max items-center justify-between md:justify-end'>
                                <form className="max-w-lg">
                                    <div className="relative">
                                        <div className="absolute inset-y-0 start-0 flex items-center pl-4 pointer-events-none">
                                            <MdSearch />
                                        </div>
                                        <input
                                            type="text"
                                            className="bg-gray-50 border border-gray-300 text-gray-900 text-md rounded-full focus:ring-blue-500 focus:border-blue-500 block w-full pl-8 p-2.5"
                                            placeholder="Search"
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                        />
                                    </div>
                                </form>
                                <button 
                                    onClick={exportAttendanceToPDF}
                                    disabled={exporting || !rawAttendanceData || rawAttendanceData.length === 0}
                                    className='hover:bg-indigo-900 rounded-full text-white mx-2 py-3 px-5 bg-[#3F3DA5] disabled:opacity-50 disabled:cursor-not-allowed'
                                >
                                    {exporting ? 'Exporting...' : 'Export PDF'}
                                </button>
                            </div>
                        </div>
                    </div>
                    <p className="text-2xl font-semibold mb-3">Attendance Register</p>
                    <div className="border rounded-3xl">
                        <DataTable
                            columns={columns}
                            data={filteredData}
                            noDataComponent = {<NoDataComponent/>}
                            pagination
                            paginationComponent={customPagination}
                            paginationRowsPerPageOptions={[10, 20, 30, 50]}
                            customStyles={customStyles}
                            progressPending={loading}
                            progressComponent={<SkeletonTable rows={5} columns={6} />}
                            highlightOnHover
                            striped
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
